import {
  CreateJobScopeChangeRequest,
  type JobScopeChangeRequestForCollaborator as JobScopeChangeProto,
  JobScopeUpdateRequest,
  ListJobScopeChangesForCollaboratorRequest,
} from '@taxfyle/api-internal/internal/job_scope_change_pb'
import { type Observable, map, share } from 'rxjs'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import {
  numberToDecimal,
  safeTimestampToDate,
  timestampToDate,
} from '@/utils/grpc-util'
import { filterNonNil } from '@/utils/rx-util'
import type { JobsApi } from '@/features/jobs/api/job-api'
import type { Job } from '@/features/jobs/Job'
import type { JobStore } from '@/features/jobs/JobStore'
import { JobScopeChange, type JobScopeChangeProps } from './JobScopeChange'
import { mapJobScopeChangeStatusProtoToJobScopeChangeStatus } from './job-scope-change-mappers'

/**
 * The store for {@link JobScopeChange}.
 */
export class JobScopeChangeStore {
  readonly jobScopeChanges = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new JobScopeChange(),
  })

  /**
   * Emits when an admin updates a job's scope.
   */
  readonly adminJobScopeUpdated$: Observable<AdminJobScopeUpdatedEvent>

  /**
   * Emits when a job scope change request is approved.
   */
  readonly jobScopeChangeApproved$: Observable<JobScopeChangeApprovedEvent>

  constructor(
    private readonly _api: JobsApi,
    private readonly _jobStore: JobStore,
  ) {
    this.adminJobScopeUpdated$ = this._api.realtime$.pipe(
      map((e) => e.getAdminJobScopeUpdated()?.getJobId()),
      filterNonNil(),
      map((jobId): AdminJobScopeUpdatedEvent => ({
        type: 'AdminJobScopeUpdated',
        jobId,
      })),
      share(),
    )

    this.jobScopeChangeApproved$ = this._api.realtime$.pipe(
      map((e) => e.getJobScopeChangeApproved()?.getJobId()),
      filterNonNil(),
      map((jobId): JobScopeChangeApprovedEvent => ({
        type: 'JobScopeChangeApproved',
        jobId,
      })),
      share(),
    )

    this.adminJobScopeUpdated$.subscribe(({ jobId }) => {
      void this._jobStore
        .fetchJob(jobId, { force: true })
        .catch(() => undefined)
      void this.fetchScopeChangesForJob({ jobId }).catch(() => undefined)
    })
  }

  /**
   * Returns the scope changes of a job.
   */
  forJob(job: Job) {
    return this.jobScopeChanges.filter((e) => e.job === job)
  }

  /**
   * Whether every scope change of a job has been resolved.
   */
  areSettledForJob(job: Job) {
    return this.forJob(job).every(
      (scopeChange) =>
        scopeChange.status === 'Accepted' ||
        scopeChange.status === 'Rejected' ||
        scopeChange.status === 'ApplyFailed',
    )
  }

  /**
   * Fetches the scope changes of a job.
   */
  async fetchScopeChangesForJob({
    jobId,
    signal,
  }: FetchScopeChangesForJobQuery) {
    const request = new ListJobScopeChangesForCollaboratorRequest().setJobId(
      Id.assert(jobId),
    )

    const response =
      await this._api.scopeChangeRequests.listJobScopeChangesForCollaborator(
        request,
      )
    signal?.throwIfAborted()

    return this.jobScopeChanges.set(
      response
        .getJobScopeChangesList()
        .map((proto) => this._mapJobScopeChangeProtoToProps(proto)),
    )
  }

  /**
   * Requests scope changes on a job.
   */
  requestScopeChanges({
    jobId,
    reason,
    scopeChangeItems,
    scopeChangeInventoryItems,
  }: RequestScopeChangesOptions) {
    const itemsRequest = scopeChangeItems.map((i) =>
      new CreateJobScopeChangeRequest.CreateJobScopeChangeItemRequest()
        .setName(i.name)
        .setQuantity(i.quantity),
    )

    const inventoryItemsRequest = scopeChangeInventoryItems.map((i) =>
      new CreateJobScopeChangeRequest.CreateJobScopeChangeInventoryItemRequest()
        .setVersionId(Id.assert(i.versionId))
        .setQuantity(i.quantity),
    )

    const request = new CreateJobScopeChangeRequest()
      .setJobId(Id.assert(jobId))
      .setReason(reason)
      .setItemsList(itemsRequest)
      .setInventoryItemsList(inventoryItemsRequest)

    return this._api.scopeChangeRequests.createJobScopeChange(request)
  }

  /**
   * Updates a job's scope as an Admin.
   */
  updateJobScopeAsAdmin({
    jobId,
    reason,
    scopeChangeCustomItemsToCreate,
    scopeChangeCustomItemsToRemove,
    scopeChangeInventoryItems,
  }: AdminJobScopeUpdateOptions) {
    const customItemsToAddRequest = scopeChangeCustomItemsToCreate.map((i) =>
      new JobScopeUpdateRequest.JobScopeUpdateCreateCustomItem()
        .setName(i.name)
        .setQuantity(i.quantity)
        .setPrice(numberToDecimal(i.price))
        .setProPrice(numberToDecimal(i.proPrice)),
    )

    const customItemsToRemoveRequest = scopeChangeCustomItemsToRemove.map((i) =>
      new JobScopeUpdateRequest.JobScopeUpdateRemoveCustomItem().setId(
        Id.assert(i.customItemId),
      ),
    )

    const inventoryItemsRequest = scopeChangeInventoryItems.map((i) =>
      new JobScopeUpdateRequest.JobScopeUpdateInventoryItem()
        .setVersionId(Id.assert(i.versionId))
        .setQuantity(i.quantity),
    )

    const request = new JobScopeUpdateRequest()
      .setJobId(Id.assert(jobId))
      .setReason(reason)
      .setCustomItemsToAddList(customItemsToAddRequest)
      .setCustomItemsToRemoveList(customItemsToRemoveRequest)
      .setInventoryItemsList(inventoryItemsRequest)

    return this._api.scopeChangeRequests.jobScopeUpdate(request)
  }

  private _mapJobScopeChangeProtoToProps(
    proto: JobScopeChangeProto,
  ): Partial<JobScopeChangeProps> {
    return {
      id: proto.getId(),
      job: this._jobStore.primeJob({ id: proto.getJobId() }),
      status: mapJobScopeChangeStatusProtoToJobScopeChangeStatus(
        proto.getStatus(),
      ),
      items: proto.getItemsList().map((item) => ({
        id: item.getId(),
        name: item.getName(),
        quantity: item.getQuantity(),
      })),
      rejectionReason: proto.getRejectionReason(),
      createdByUserPublicId: proto.getCreatedByUserPublicId(),
      createTime: safeTimestampToDate(proto.getCreateTime()),
      latestResponseTime:
        timestampToDate(proto.getLatestResponseTime()) ?? null,
    }
  }
}

/** @see {JobScopeChangeStore.fetchScopeChangesForJob} */
export interface FetchScopeChangesForJobQuery {
  jobId: Id
  signal?: AbortSignal
}

/**
 * Options for updating a job's scope as an admin.
 */
export interface AdminJobScopeUpdateOptions {
  jobId: Id
  reason: string
  scopeChangeCustomItemsToCreate: AdminJobScopeUpdateCustomItemToCreateParams[]
  scopeChangeCustomItemsToRemove: AdminJobScopeUpdateCustomItemToRemoveParams[]
  scopeChangeInventoryItems: AdminJobScopeUpdateInventoryItemParams[]
}

/**
 * Options for requesting scope changes on a job.
 */
export interface RequestScopeChangesOptions {
  jobId: Id
  reason: string
  scopeChangeItems: ScopeChangeParams[]
  scopeChangeInventoryItems: ScopeChangeInventoryParams[]
}

/**
 * An item in a scope change request.
 */
export interface ScopeChangeParams {
  name: string
  quantity: number
}

/**
 * An inventory item in a scope change request.
 */
export interface ScopeChangeInventoryParams {
  versionId: Id
  quantity: number
}

/**
 * A custom item an Admin adds to a job's scope.
 */
export interface AdminJobScopeUpdateCustomItemToCreateParams {
  name: string
  quantity: number
  price: number
  proPrice: number
}

/**
 * An inventory item an Admin sets on a job's scope.
 */
export interface AdminJobScopeUpdateInventoryItemParams {
  versionId: Id
  quantity: number
}

/**
 * A custom item an Admin removes from a job's scope.
 */
export interface AdminJobScopeUpdateCustomItemToRemoveParams {
  customItemId: Id
}

/**
 * An Admin updated a job's scope.
 */
export interface AdminJobScopeUpdatedEvent {
  type: 'AdminJobScopeUpdated'
  jobId: Id
}

/**
 * A job's scope change was approved.
 */
export interface JobScopeChangeApprovedEvent {
  type: 'JobScopeChangeApproved'
  jobId: Id
}
